import axios from 'axios';
import { secureLocalStorage } from '../../utils/crypto';
import { getTenantId } from '../tenant/tenantService.js';
import { getUserProfile } from '../auth/userService.js';

const REACT_APP_CMS_URL = process.env.REACT_APP_CMS_URL;

const getApiHeaders = (requireAuth = false) => {
  const headers = {
    'X-Tenant-Id': getTenantId(),
    'Content-Type': 'application/json',
  };
  
  if (requireAuth) {
    const token = secureLocalStorage.getItem('idToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

const apiCms = axios.create({
  baseURL: REACT_APP_CMS_URL,
});

/**
 * Busca assets ativos do CMS filtrados por assetType
 * @param {number|number[]} assetType - Tipo(s) de asset para filtrar
 * @param {Object} options - Opções de busca
 * @param {string} options.language - Idioma (padrão: 'pt-BR')
 * @param {string} options.profile - Perfil do usuário (padrão: 'gold')
 * @param {boolean} options.isPublic - Se deve buscar apenas assets públicos (padrão: true)
 * @param {boolean} options.requireAuth - Se requer autenticação (padrão: false)
 * @returns {Promise<Array>} Lista de assets
 */
export const getAssetsByType = async (assetType, options = {}) => {
  try {
    const {
      language = 'pt-BR',
      profile = getUserProfile(),
      isPublic = true, // Padrão alterado para true (endpoint público)
      requireAuth = false,
    } = options;

    const assetTypes = Array.isArray(assetType) ? assetType : [assetType];
    
    // Sempre usa o endpoint público com o parâmetro type
    const endpoint = '/api/Assets/actives/public';
    
    // Se temos múltiplos assetTypes, busca cada um separadamente e combina
    if (assetTypes.length > 1) {
      const promises = assetTypes.map(type => 
        apiCms.get(endpoint, {
          params: {
            type,
            language,
          },
          headers: getApiHeaders(requireAuth),
        })
      );
      
      const responses = await Promise.all(promises);
      const allAssets = responses.flatMap(response => response.data);
      
      return allAssets;
    } else {
      // Caso único assetType
      const params = {
        type: assetTypes[0],
        language,
      };

      const response = await apiCms.get(endpoint, {
        params,
        headers: getApiHeaders(requireAuth),
      });

      return response.data || [];
    }
  } catch (error) {
    console.error(`Error fetching assets for type ${assetType}:`, error);
    throw error;
  }
};

/**
 * Busca um asset específico por ID
 * @param {string} assetId - ID do asset
 * @param {boolean} requireAuth - Se requer autenticação
 * @returns {Promise<Object>} Asset
 */
export const getAssetById = async (assetId, requireAuth = false) => {
  try {
    const response = await apiCms.get(`/api/Assets/${assetId}`, {
      headers: getApiHeaders(requireAuth),
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching asset ${assetId}:`, error);
    throw error;
  }
};

/**
 * Busca assets ativos por múltiplos tipos
 * @param {number[]} assetTypes - Array de tipos de assets
 * @param {Object} options - Opções de busca (mesmas de getAssetsByType)
 * @returns {Promise<Array>} Lista de assets
 */
export const getAssetsByTypes = async (assetTypes, options = {}) => {
  return getAssetsByType(assetTypes, options);
};

export default {
  getAssetsByType,
  getAssetById,
  getAssetsByTypes,
};

